import {
  SET_ACTIVE_MODE,
  SET_SELECTED_MODEL,
  SET_SELECTED_MODEL_PRESERVE_DATA,
  UPDATE_FORM_DATA,
  RESET_FORM_DATA,
  START_GENERATION,
  UPDATE_GENERATION_PROGRESS,
  GENERATION_SUCCESS,
  GENERATION_FAILED,
  RESET_GENERATION,
  ADD_TO_GENERATION_QUEUE,
  UPDATE_QUEUE_ITEM_PROGRESS,
  COMPLETE_QUEUE_ITEM,
  FAIL_QUEUE_ITEM,
  REMOVE_FROM_GENERATION_QUEUE,
  SELECT_QUEUE_ITEM,
  CLEAR_QUEUE_SELECTION,
  RETRY_QUEUE_ITEM,
  SET_VALIDATION_ERRORS,
  CLEAR_VALIDATION_ERRORS,
  SET_SIDEBAR_EXPANDED,
  TOGGLE_SIDEBAR_SWAP,
  SAVE_FORM_STATE_FOR_MODE,
  RESTORE_FORM_STATE_FOR_MODE,
  FETCH_GALLERY_REQUESTED,
  FETCH_GALLERY_SUCCESS,
  FETCH_GALLERY_FAILED,
  RESET_GALLERY,
  SET_GALLERY_VIEW_MODE,
  SET_GALLERY_FILTER,
  SELECT_GALLERY_ITEM,
  CLEAR_GALLERY_SELECTION,
  ADD_RESULT_TO_GALLERY_CACHE,
} from '../types/create.type'

// Helper function to get default sub-mode for modes that have multiple models
const getDefaultSubModeForMode = (mode) => {
  switch (mode) {
    case '3d':
      return 'text-to-3d'
    case 'video':
      return 'text-to-video'
    default:
      return null
  }
}

// Model aliases for different creation modes
export const MODEL_ALIASES = {
  '3d': {
    'text-to-3d': 'text-to-3d',
    'image-to-3d': 'image-to-3d',
  },
  image: {
    'text-to-image': 'styley-text-to-image',
  },
  video: {
    'text-to-video': 'text-to-video',
    'image-to-video': 'photo-to-video',
  },
  music: {
    'text-to-music': 'music-maker',
  },
}

// Default form values for each model
export const DEFAULT_FORM_VALUES = {
  'text-to-3d': {
    prompt: '',
    negativePrompt: 'low quality, low resolution, low poly, ugly',
    outputFormat: 'glb',
    style: 'realistic',
  },
  'image-to-3d': {
    imageUrl: '',
    outputFormat: 'glb',
    style: 'realistic',
  },
  'styley-text-to-image': {
    prompt: '',
    aspectRatio: '1:1',
    magicPrompt: 'ON',
    styleType: 'RENDER_3D',
  },
  'text-to-video': {
    prompt: '',
    aspectRatio: '1:1',
    loop: 'true',
    duration: 5,
  },
  'photo-to-video': {
    imageUrl: '',
    aspectRatio: '1:1',
    loop: 'true',
    duration: 5,
  },
  'music-maker': {
    prompt: '',
    duration: 30,
    genre: 'Pop',
  },
}

const initialState = {
  // UI State
  activeMode: '3d',
  leftSidebarExpanded: true,
  rightSidebarExpanded: true,
  swappedSidebars: false,

  // Model and Form State
  selectedModel: null,
  selectedSubMode: null, // For modes that have multiple options like 3D (text-to-3d, image-to-3d)
  formData: {},

  // Form state persistence - stores form data for each mode and sub-mode
  savedFormStates: {
    '3d': {
      'text-to-3d': null,
      'image-to-3d': null,
    },
    image: null,
    video: {
      'text-to-video': null,
      'image-to-video': null,
    },
    music: null,
  },

  // Generation State (old single generation system - kept for compatibility)
  isGenerating: false,
  generationProgress: null,
  generationResults: [],
  generationError: null,
  currentGeneration: null,

  // New Generation Queue System
  generationQueue: {
    items: [], // Array of generation items
    selectedItem: null, // Currently selected queue item for details view
    maxConcurrent: 3, // Maximum concurrent generations
    activeCount: 0, // Current number of active generations
  },

  // Validation State
  validationErrors: {},

  // Gallery State
  gallery: {
    viewMode: 'card', // 'card' or 'list'
    filter: 'my', // 'my' or 'public'
    loading: false,
    error: null,
    currentRequest: null,
    selectedItem: null, // Currently selected gallery item
    cache: {
      // Structure: {cacheKey: {items: [], total: 0, hasMore: boolean, lastFetch: timestamp}}
    },
  },

  // Credit cost based on mode
  creditCosts: {
    '3d': 10,
    image: 5,
    video: 15,
    music: 20,
  },
}

const createReducer = (state = initialState, action) => {
  // Handle gallery actions
  if (
    [
      SET_GALLERY_VIEW_MODE,
      SET_GALLERY_FILTER,
      RESET_GALLERY,
      FETCH_GALLERY_REQUESTED,
      FETCH_GALLERY_SUCCESS,
      FETCH_GALLERY_FAILED,
      SELECT_GALLERY_ITEM,
      CLEAR_GALLERY_SELECTION,
      ADD_RESULT_TO_GALLERY_CACHE,
    ].includes(action.type)
  ) {
    return galleryReducer(state, action)
  }

  // Handle other create actions
  switch (action.type) {
    case SET_ACTIVE_MODE:
      return {
        ...state,
        activeMode: action.payload,
        selectedModel: null,
        selectedSubMode: null,
        formData: {},
        validationErrors: {},
      }

    case SET_SELECTED_MODEL:
      const { model, subMode } = action.payload

      // Only reset formData if it's empty, switching models, or coming from a different mode
      const hasExistingFormData = state.formData && Object.keys(state.formData).length > 0
      const isChangingModelType = state.selectedModel?.alias !== model?.alias
      const isChangingMode = action.resetFormData === true

      let newFormData = state.formData

      if (!hasExistingFormData || isChangingMode || isChangingModelType) {
        // Get defaults but merge with any existing data that's compatible
        const defaults = DEFAULT_FORM_VALUES[model?.alias] || {}
        newFormData = hasExistingFormData && !isChangingMode ? { ...defaults, ...state.formData } : defaults
      }

      return {
        ...state,
        selectedModel: model,
        selectedSubMode: subMode,
        formData: newFormData,
        validationErrors: {},
      }

    case SET_SELECTED_MODEL_PRESERVE_DATA:
      const { model: preserveModel, subMode: preserveSubMode } = action.payload
      return {
        ...state,
        selectedModel: preserveModel,
        selectedSubMode: preserveSubMode,
        // Keep existing form data - don't reset it
        validationErrors: {},
      }

    case UPDATE_FORM_DATA:
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload,
        },
      }

    case RESET_FORM_DATA:
      return {
        ...state,
        formData: state.selectedModel ? DEFAULT_FORM_VALUES[state.selectedModel.alias] || {} : {},
        validationErrors: {},
      }

    case START_GENERATION:
      return {
        ...state,
        isGenerating: true,
        generationProgress: {
          percentage: 0,
          message: 'Initializing...',
          logs: '',
        },
        generationError: null,
        currentGeneration: action.payload,
      }

    case UPDATE_GENERATION_PROGRESS:
      return {
        ...state,
        generationProgress:
          action.payload === null
            ? null
            : {
                ...state.generationProgress,
                ...action.payload,
              },
      }

    case GENERATION_SUCCESS:
      return {
        ...state,
        isGenerating: false,
        generationProgress: null, // Clear progress to close modal
        generationResults: [action.payload, ...state.generationResults],
        currentGeneration: null,
      }

    case GENERATION_FAILED:
      return {
        ...state,
        isGenerating: false,
        generationProgress: null, // Clear progress to close modal
        generationError: action.payload,
        currentGeneration: null,
      }

    case RESET_GENERATION:
      return {
        ...state,
        isGenerating: false,
        generationProgress: null,
        generationError: null,
        currentGeneration: null,
      }

    case SET_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: action.payload,
      }

    case CLEAR_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: {},
      }

    // New Generation Queue System Cases
    case ADD_TO_GENERATION_QUEUE:
      return {
        ...state,
        generationQueue: {
          ...state.generationQueue,
          items: [...state.generationQueue.items, action.payload],
          activeCount:
            action.payload.status === 'running'
              ? state.generationQueue.activeCount + 1
              : state.generationQueue.activeCount,
        },
      }

    case UPDATE_QUEUE_ITEM_PROGRESS:
      const { itemId, progress } = action.payload
      return {
        ...state,
        generationQueue: {
          ...state.generationQueue,
          items: state.generationQueue.items.map((item) =>
            item.id === itemId ? { ...item, progress: { ...item.progress, ...progress } } : item
          ),
        },
      }

    case COMPLETE_QUEUE_ITEM:
      const { itemId: completeItemId, result } = action.payload
      return {
        ...state,
        generationQueue: {
          ...state.generationQueue,
          items: state.generationQueue.items.map((item) =>
            item.id === completeItemId
              ? {
                  ...item,
                  status: 'completed',
                  result,
                  progress: { ...item.progress, percentage: 100, message: 'Completed!' },
                }
              : item
          ),
          activeCount: Math.max(0, state.generationQueue.activeCount - 1),
        },
        generationResults: [result, ...state.generationResults],
      }

    case FAIL_QUEUE_ITEM:
      const { itemId: failItemId, error } = action.payload
      return {
        ...state,
        generationQueue: {
          ...state.generationQueue,
          items: state.generationQueue.items.map((item) =>
            item.id === failItemId
              ? { ...item, status: 'failed', error, progress: { ...item.progress, message: 'Failed' } }
              : item
          ),
          activeCount: Math.max(0, state.generationQueue.activeCount - 1),
        },
      }

    case REMOVE_FROM_GENERATION_QUEUE:
      const removedItem = state.generationQueue.items.find((item) => item.id === action.payload)
      return {
        ...state,
        generationQueue: {
          ...state.generationQueue,
          items: state.generationQueue.items.filter((item) => item.id !== action.payload),
          selectedItem:
            state.generationQueue.selectedItem?.id === action.payload ? null : state.generationQueue.selectedItem,
          activeCount:
            removedItem?.status === 'running'
              ? Math.max(0, state.generationQueue.activeCount - 1)
              : state.generationQueue.activeCount,
        },
      }

    case SELECT_QUEUE_ITEM:
      return {
        ...state,
        generationQueue: {
          ...state.generationQueue,
          selectedItem: action.payload,
        },
        // Clear gallery selection when selecting queue item
        gallery: {
          ...state.gallery,
          selectedItem: null,
        },
      }

    case CLEAR_QUEUE_SELECTION:
      return {
        ...state,
        generationQueue: {
          ...state.generationQueue,
          selectedItem: null,
        },
      }

    case RETRY_QUEUE_ITEM:
      const { itemId: retryItemId } = action.payload
      return {
        ...state,
        generationQueue: {
          ...state.generationQueue,
          items: state.generationQueue.items.map((item) =>
            item.id === retryItemId
              ? {
                  ...item,
                  status: 'running',
                  error: null,
                  progress: { percentage: 0, message: 'Retrying...', logs: '', timeRemaining: 'Calculating...' },
                  retryCount: (item.retryCount || 0) + 1,
                }
              : item
          ),
          activeCount: state.generationQueue.activeCount + 1,
        },
      }

    case SET_SIDEBAR_EXPANDED:
      const { sidebar, expanded } = action.payload
      return {
        ...state,
        [`${sidebar}SidebarExpanded`]: expanded,
      }

    case TOGGLE_SIDEBAR_SWAP:
      return {
        ...state,
        swappedSidebars: !state.swappedSidebars,
      }

    case SAVE_FORM_STATE_FOR_MODE:
      const {
        mode: saveMode,
        formData: saveFormData,
        selectedModel: saveModel,
        selectedSubMode: saveSubMode,
      } = action.payload

      // Determine the correct path for saving
      let savePath
      if (saveMode === '3d' || saveMode === 'video') {
        savePath = `savedFormStates.${saveMode}.${saveSubMode || getDefaultSubModeForMode(saveMode)}`
      } else {
        savePath = `savedFormStates.${saveMode}`
      }

      return {
        ...state,
        savedFormStates: {
          ...state.savedFormStates,
          [saveMode]:
            saveMode === '3d' || saveMode === 'video'
              ? {
                  ...state.savedFormStates[saveMode],
                  [saveSubMode || getDefaultSubModeForMode(saveMode)]: {
                    formData: saveFormData,
                    selectedModel: saveModel,
                    timestamp: Date.now(),
                  },
                }
              : {
                  formData: saveFormData,
                  selectedModel: saveModel,
                  timestamp: Date.now(),
                },
        },
      }

    case RESTORE_FORM_STATE_FOR_MODE:
      const { mode: restoreMode } = action.payload
      // Restoration logic is handled in the action creator
      return state

    default:
      return state
  }
}

// Gallery reducer cases
const galleryReducer = (state, action) => {
  switch (action.type) {
    case SET_GALLERY_VIEW_MODE:
      return {
        ...state,
        gallery: {
          ...state.gallery,
          viewMode: action.payload,
        },
      }

    case SET_GALLERY_FILTER:
      return {
        ...state,
        gallery: {
          ...state.gallery,
          filter: action.payload,
        },
      }

    case RESET_GALLERY:
      return {
        ...state,
        gallery: {
          ...initialState.gallery,
        },
      }

    case FETCH_GALLERY_REQUESTED:
      const { cacheKey } = action.payload
      return {
        ...state,
        gallery: {
          ...state.gallery,
          loading: true,
          error: null,
          currentRequest: cacheKey,
        },
      }

    case FETCH_GALLERY_SUCCESS:
      const { cacheKey: successCacheKey, items, total, from, hasMore } = action.payload

      const existingCache = state.gallery.cache[successCacheKey]
      const isLoadMore = from > 0 && existingCache

      return {
        ...state,
        gallery: {
          ...state.gallery,
          loading: false,
          error: null,
          currentRequest: null,
          cache: {
            ...state.gallery.cache,
            [successCacheKey]: {
              items: isLoadMore ? [...(existingCache.items || []), ...items] : items,
              total,
              hasMore,
              lastFetch: Date.now(),
            },
          },
        },
      }

    case FETCH_GALLERY_FAILED:
      const { cacheKey: failedCacheKey, error } = action.payload
      return {
        ...state,
        gallery: {
          ...state.gallery,
          loading: false,
          error,
          currentRequest: null,
        },
      }

    case SELECT_GALLERY_ITEM:
      return {
        ...state,
        gallery: {
          ...state.gallery,
          selectedItem: action.payload,
        },
        // Clear queue selection when selecting gallery item
        generationQueue: {
          ...state.generationQueue,
          selectedItem: null,
        },
      }

    case CLEAR_GALLERY_SELECTION:
      return {
        ...state,
        gallery: {
          ...state.gallery,
          selectedItem: null,
        },
      }

    case ADD_RESULT_TO_GALLERY_CACHE:
      const { result, activeMode, modelId } = action.payload

      // Generate the cache key for "My Results" (private)
      const myResultsCacheKey = `${activeMode}_${modelId}_private`

      // Transform the generation result to gallery item format
      const galleryItem = {
        id: result.deploymentId || result.id,
        title: result.prompt || 'Untitled',
        files: result.files || [],
        createdAt: result.createdAt,
        duration: result.duration || 0,
        rating: result.rating || 0,
        visibility: 'private',
        modelId: modelId,
        modelName: result.model?.name || '',
        args: result.args || {},
        status: 'complete',
        creditsUsed: 0,
      }

      // Get existing cache data
      const existingMyResultsCache = state.gallery.cache[myResultsCacheKey]

      // Add the new result at the top of the list and update the cache
      return {
        ...state,
        gallery: {
          ...state.gallery,
          cache: {
            ...state.gallery.cache,
            [myResultsCacheKey]: {
              items: [galleryItem, ...(existingMyResultsCache?.items || [])],
              total: (existingMyResultsCache?.total || 0) + 1,
              hasMore: existingMyResultsCache?.hasMore || false,
              lastFetch: Date.now(),
            },
          },
        },
      }

    default:
      return state
  }
}

export default createReducer
